// SPDX-License-Identifier: GPL-3.0-or-later
// A pool of short-lived particles in one point cloud. The pool has a hard cap. A burst that does
// not fit is cut: the cap is never exceeded. `update` allocates nothing: it only writes into the
// typed arrays. A particle that dies is replaced by the last live one, so the live ones stay
// at the front and the draw range is the live count.

import type { Color } from "three";
import { createPointCloud, type PointCloud } from "./points.ts";

/** The look and the motion of one burst. Sizes are in table units, speed in units per second. */
export interface BurstShape {
  readonly speed: number;
  readonly size: number;
  readonly endSize: number;
  readonly lifeMs: number;
  /** Units per second squared, down. 0 for a halo. */
  readonly gravity: number;
  /** Share of the speed that goes up, from 0 to 1. */
  readonly lift: number;
}

/** The longest step that `update` integrates, in ms. A longer pause is cut. */
export const MAX_STEP_MS = 100;

/** A seeded random source with the range [0, 1). The effects need no secure random. */
export function createRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The pool. */
export interface ParticlePool {
  readonly cloud: PointCloud;
  readonly cap: number;
  /** Number of live particles. */
  readonly live: number;
  /** Start a burst at a point (three.js frame). Returns the number of particles that started. */
  burst(x: number, y: number, z: number, color: Color, count: number, shape: BurstShape): number;
  /** Move the particles on by `dtMs`. */
  update(dtMs: number): void;
  /** Remove every particle. */
  clear(): void;
}

interface PoolState {
  readonly cap: number;
  readonly random: () => number;
  readonly cloud: PointCloud;
  readonly vel: Float32Array;
  readonly base: Float32Array;
  readonly age: Float32Array;
  readonly life: Float32Array;
  readonly size0: Float32Array;
  readonly size1: Float32Array;
  readonly grav: Float32Array;
  /** The per-particle arrays with three values each. Listed once, so a move makes no array. */
  readonly triples: readonly Float32Array[];
  /** The per-particle arrays with one value each. */
  readonly singles: readonly Float32Array[];
  live: number;
}

const at = (array: Float32Array, i: number): number => array[i] as number;

/** Copy the particle at `from` over the slot `to`. */
function particlesMove(st: PoolState, from: number, to: number): void {
  for (const array of st.triples) array.copyWithin(to * 3, from * 3, from * 3 + 3);
  for (const array of st.singles) array.copyWithin(to, from, from + 1);
}

/** Write a new particle at slot `i`. */
function particlesSpawn(
  st: PoolState,
  i: number,
  x: number,
  y: number,
  z: number,
  color: Color,
  shape: BurstShape,
): void {
  const angle = st.random() * Math.PI * 2;
  const reach = shape.speed * (0.25 + 0.75 * st.random());
  const up = shape.lift * reach * (0.4 + 0.6 * st.random());
  const flat = reach * (1 - shape.lift * 0.5);
  st.cloud.position[i * 3] = x;
  st.cloud.position[i * 3 + 1] = y;
  st.cloud.position[i * 3 + 2] = z;
  st.vel[i * 3] = Math.cos(angle) * flat;
  st.vel[i * 3 + 1] = up;
  st.vel[i * 3 + 2] = Math.sin(angle) * flat;
  st.base[i * 3] = color.r;
  st.base[i * 3 + 1] = color.g;
  st.base[i * 3 + 2] = color.b;
  st.age[i] = 0;
  st.life[i] = shape.lifeMs * (0.7 + 0.3 * st.random());
  st.size0[i] = shape.size;
  st.size1[i] = shape.endSize;
  st.grav[i] = shape.gravity;
}

/** Move the particle at slot `i` on by `dtS` seconds, and fade and size it. */
function particlesAdvance(st: PoolState, i: number, dtS: number): void {
  const t = at(st.age, i) / at(st.life, i);
  const fade = (1 - t) * (1 - t);
  st.vel[i * 3 + 1] = at(st.vel, i * 3 + 1) - at(st.grav, i) * dtS;
  for (let k = 0; k < 3; k++) {
    st.cloud.position[i * 3 + k] = at(st.cloud.position, i * 3 + k) + at(st.vel, i * 3 + k) * dtS;
    st.cloud.color[i * 3 + k] = at(st.base, i * 3 + k) * fade;
  }
  const s0 = at(st.size0, i);
  st.cloud.size[i] = s0 + (at(st.size1, i) - s0) * t;
}

/** Start a burst. The count is cut to the room left in the pool. */
function particlesBurst(
  st: PoolState,
  x: number,
  y: number,
  z: number,
  color: Color,
  count: number,
  shape: BurstShape,
): number {
  const want = Number.isFinite(count) ? Math.floor(count) : 0;
  const room = Math.max(0, Math.min(want, st.cap - st.live));
  for (let n = 0; n < room; n++) {
    particlesSpawn(st, st.live, x, y, z, color, shape);
    particlesAdvance(st, st.live, 0);
    st.live++;
  }
  st.cloud.commit(st.live);
  return room;
}

/** The step that `update` integrates. Bad values count as 0 and long pauses are cut. */
function particlesStepMs(dtMs: number): number {
  return Math.min(Math.max(Number.isFinite(dtMs) ? dtMs : 0, 0), MAX_STEP_MS);
}

/** Remove the particle at slot `i`. The last live particle takes its place. */
function particlesRemove(st: PoolState, i: number): void {
  st.live--;
  particlesMove(st, st.live, i);
}

function particlesUpdate(st: PoolState, dtMs: number): void {
  const step = particlesStepMs(dtMs);
  let i = 0;
  while (i < st.live) {
    st.age[i] = at(st.age, i) + step;
    if (at(st.age, i) >= at(st.life, i)) {
      particlesRemove(st, i);
    } else {
      particlesAdvance(st, i, step / 1000);
      i++;
    }
  }
  st.cloud.commit(st.live);
}

/** Make a pool with room for `cap` particles. */
export function createParticlePool(cap: number, random: () => number): ParticlePool {
  const cloud = createPointCloud(cap);
  const vel = new Float32Array(cap * 3);
  const base = new Float32Array(cap * 3);
  const age = new Float32Array(cap);
  const life = new Float32Array(cap);
  const size0 = new Float32Array(cap);
  const size1 = new Float32Array(cap);
  const grav = new Float32Array(cap);

  const st: PoolState = {
    cap,
    random,
    cloud,
    vel,
    base,
    age,
    life,
    size0,
    size1,
    grav,
    triples: [cloud.position, vel, base],
    singles: [age, life, size0, size1, grav],
    live: 0,
  };

  return {
    cloud,
    cap,
    get live() {
      return st.live;
    },
    burst(x, y, z, color, count, shape) {
      return particlesBurst(st, x, y, z, color, count, shape);
    },
    update(dtMs) {
      particlesUpdate(st, dtMs);
    },
    clear() {
      st.live = 0;
      cloud.commit(0);
    },
  };
}
